import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../hooks/useAuth";
import { checkEmailConfirmationStatus, resendEmailConfirmation } from "../services/onboardingApiService";
import { HeadingLarge, LabelMedium } from "../components/OnboardingTypography";
import { OnboardingButtonPrimary } from "../components/OnboardingButton";
import OnboardingHelpLink from "../components/OnboardingHelpLink";
import { FlexColumn, MessageBox, OnboardingCard, THEME } from "../components/OnboardingLayoutComponents";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ERROR_CONTEXTS } from "../utils/errorMessages";

// Replaced with OnboardingCard, MessageBox, and FlexColumn from OnboardingLayoutComponents


const EmailAddress = styled(LabelMedium)`
  font-weight: 600;
  font-size: 18px;
  color: ${THEME.colors.info};
  margin-top: ${THEME.spacing.sm};
`;

const CountdownText = styled(LabelMedium)`
  color: ${THEME.colors.textMuted};
  font-size: 14px;
`;



const PrimaryButton = styled(OnboardingButtonPrimary)`
  min-width: 200px;
`;

const ResendButton = styled(OnboardingButtonPrimary)`
  min-width: 200px;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.colors.text};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  font-family: inherit;
  transition: ${THEME.transitions.fast};
  
  &:hover {
    color: ${THEME.colors.primaryDark};
  }
`;

const DisabledButton = styled.button`
  min-width: 200px;
  background-color: ${THEME.colors.primary};
  color: ${THEME.colors.text};
  border: none;
  padding: ${THEME.spacing.md} ${THEME.spacing.xxl};
  border-radius: ${THEME.borderRadius.sm};
  font-size: 14px;
  font-family: inherit;
  opacity: 0.5;
  cursor: not-allowed;
`;

const EnabledButton = styled.button`
  min-width: 200px;
  background-color: ${THEME.colors.primary};
  color: ${THEME.colors.text};
  border: none;
  padding: ${THEME.spacing.md} ${THEME.spacing.xxl};
  border-radius: ${THEME.borderRadius.sm};
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: ${THEME.transitions.default};
  
  &:hover {
    background-color: ${THEME.colors.selectedBg};
  }
`;



const SuccessContainer = styled.div`
  text-align: left;
  padding: ${THEME.spacing.mega};
  max-width: 800px;
  margin: 0 auto;
`;

const SuccessMessage = styled(LabelMedium)`
  margin-top: ${THEME.spacing.lg};
  color: ${THEME.colors.success};
`;

const StatusMessage = styled(LabelMedium)`
  margin-top: ${THEME.spacing.sm};
  font-style: italic;
`;

const EmailVerificationContainer = ({ 
  credentials, 
  goToNextStep, 
  goToPreviousStep 
}) => {
  const [pollingStatus, setPollingStatus] = useState("pending");
  const [countdown, setCountdown] = useState(10);
  const [isPolling, setIsPolling] = useState(true);
  
  const navigate = useNavigate();
  const auth = useAuth();
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  
  // Centralized error handling
  const { handleError, setCustomError, clearError, getMessageBoxProps, errorMessage } = useErrorHandler(ERROR_CONTEXTS.EMAIL_VERIFICATION);

  // Polling function
  const checkEmailStatus = async () => {
    try {
      clearError();
      const response = await checkEmailConfirmationStatus(credentials);
      
      if (response.data.status === "confirmed" && response.data.emailConfirmed) {
        // Email confirmed, stop polling and authenticate
        setPollingStatus("confirmed");
        setIsPolling(false);
        
        // Let the useEffect cleanup handle interval clearing
        // Authenticate with the token
        auth.tokenSignIn(response.data.token);
        
      }
      // If status is still "pending", continue polling
    } catch (error) {
      // Use centralized error handling for polling
      handleError(error, ERROR_CONTEXTS.EMAIL_VERIFICATION);
    }
  };

  // Consolidated interval management - handles both mounting and polling state changes
  useEffect(() => {
    // Clear any existing intervals first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Only set up intervals if we have credentials and are actively polling
    if (!credentials || !credentials.email || !credentials.password) {
      setCustomError("Mangler påloggingsinformasjon. Vennligst gå tilbake og prøv igjen.", "warning");
      return;
    }

    if (isPolling) {
      // Initial check
      checkEmailStatus();

      // Set up polling interval (10 seconds)
      intervalRef.current = setInterval(checkEmailStatus, 10000);

      // Set up countdown timer
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 10; // Reset countdown
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup function - always called when effect re-runs or component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [credentials, isPolling]); // React to both credentials and polling state changes

  const handleResendEmail = async () => {
    try {
      clearError();
      const response = await resendEmailConfirmation(credentials);
      
      // Clear any existing errors on success
      clearError();
      
      // Reset polling state - the useEffect will handle restarting intervals
      setPollingStatus("pending");
      setCountdown(10);
      
      // Ensure polling is active (this will trigger useEffect to restart intervals)
      if (!isPolling) {
        setIsPolling(true);
      }
      
      // No need to trigger immediate check - useEffect will handle it when isPolling changes
    } catch (error) {
      // Use centralized error handling
      handleError(error, ERROR_CONTEXTS.EMAIL_RESEND);
    }
  };


  const handleProceedToOnboarding = () => {
    navigate("/onboarding/setup");
  };


  return (
    <OnboardingCard 
      padding={THEME.spacing.mega} 
      maxWidth="800px" 
      margin="0 auto" 
      style={{ textAlign: "left" }}
    >
      <FlexColumn gap={THEME.spacing.xxxl} alignItems="flex-start">
        <HeadingLarge>Steg 3: Kom i gang</HeadingLarge>
        
        <div>
          <LabelMedium>
            Før du kan logge inn må du verifisere din e-post. Vi har sendt deg en lenke til: <strong>{credentials?.email}</strong>.
          </LabelMedium>
        </div>

        <MessageBox 
          variant="info" 
          padding={THEME.spacing.lg} 
          margin="0"
          style={{ width: "100%" }}
        >
          <LabelMedium style={{ marginBottom: pollingStatus === "confirmed" ? "0" : THEME.spacing.lg, fontWeight: "bold" }}>
            {pollingStatus === "confirmed" ? "✅  E-post bekreftet" : "Vi venter på at du bekrefter e-posten din."}
          </LabelMedium>
          
          {isPolling && (
            <CountdownText>
              Sjekker automatisk om {countdown} sekunder...
            </CountdownText>
          )}
          
          {pollingStatus !== "confirmed" && (
            <div style={{ marginTop: THEME.spacing.lg }}>
              <TextButton onClick={handleResendEmail}>
                Klikk her om du ikke har mottatt den
              </TextButton>
            </div>
          )}
        </MessageBox>

        {getMessageBoxProps() && (
          <MessageBox 
            {...getMessageBoxProps()} 
            padding={THEME.spacing.lg} 
            margin="0"
            style={{ width: "100%" }}
          />
        )}

        <div>
          {pollingStatus === "confirmed" ? (
            <EnabledButton onClick={handleProceedToOnboarding}>
              <LabelMedium>Gå til kundeportalen</LabelMedium>
            </EnabledButton>
          ) : (
            <DisabledButton disabled>
              <LabelMedium>Gå til kundeportalen</LabelMedium>
            </DisabledButton>
          )}
        </div>

        <OnboardingHelpLink />
      </FlexColumn>
    </OnboardingCard>
  );
};

export default EmailVerificationContainer;